import { Link } from "react-router-dom";
import { MdDelete, MdSave } from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Dialog from "components/dialog";
import Tabs, { TabPanel } from "components/tabs";
import Skeleton from "components/skeleton/Skeleton";
import { ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { getApiErrorMessage } from "api";
import { formatNumber } from "utils/format";
import useCampaignEditor from "./campaign-editor/useCampaignEditor";
import { EDITOR_TABS } from "./campaign-editor/validation";
import DetailsSection from "./campaign-editor/DetailsSection";
import QuestionsSection, { SaveTemplateDialog } from "./campaign-editor/QuestionsSection";
import RecipientsSection from "./campaign-editor/RecipientsSection";
import DeliverySection from "./campaign-editor/DeliverySection";
import CampaignSummary from "./campaign-editor/CampaignSummary";

const TABS_ID = "campaign-editor";
const FORM_ID = "campaign-editor-form";
const CAMPAIGNS_PATH = "/admin/heartbeat";

const SECTIONS = {
  details: DetailsSection,
  questions: QuestionsSection,
  recipients: RecipientsSection,
  delivery: DeliverySection,
};

const EditorSkeleton = () => (
  <Card padding="md" className="gap-5" aria-busy="true">
    <Skeleton className="h-6 w-48" />
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-10 w-full rounded-control" />
      </div>
    ))}
  </Card>
);

const StatusConfirmDialog = ({ editor }) => {
  const { formData, saving, showStatusConfirmModal, setShowStatusConfirmModal, performSave } = editor;

  return (
    <Dialog
      isOpen={showStatusConfirmModal}
      onClose={() => setShowStatusConfirmModal(false)}
      size="sm"
      title={`Activate "${formData.name}"?`}
      footer={
        <>
          <Button variant="secondary" disabled={saving} onClick={() => performSave("draft")}>
            Keep as Draft
          </Button>
          <Button loading={saving} onClick={() => performSave("active")}>
            {saving ? "Saving…" : "Activate Now"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">
        This campaign is set to <span className="font-semibold text-fg">Draft</span>. Draft
        campaigns are not active and won't send surveys automatically. Would you like to activate
        it now?
      </p>
    </Dialog>
  );
};

const CreateEditCampaign = () => {
  const editor = useCampaignEditor();
  const {
    isEdit,
    formRef,
    formData,
    loading,
    loadError,
    fetchCampaign,
    saving,
    deleting,
    handleDelete,
    handleSubmit,
    activeTab,
    setActiveTab,
    selectedRecipients,
    filterRecipientCount,
    recipientCountCache,
  } = editor;

  const title = isEdit ? "Edit Campaign" : "Create Campaign";
  const ready = !loading && !loadError;

  const recipientCount =
    formData.recipientMode === "custom" && filterRecipientCount !== null
      ? formatNumber(filterRecipientCount)
      : selectedRecipients.length > 0
      ? formatNumber(selectedRecipients.length)
      : undefined;
  const tabs = EDITOR_TABS.map((tab) =>
    tab.id === "recipients" ? { ...tab, count: recipientCount } : tab
  );

  const ActiveSection = SECTIONS[activeTab] || DetailsSection;

  return (
    <PageContainer>
      <PageHeader
        title={title}
        description={
          isEdit ? "Update your Heartbeat survey campaign" : "Set up a new Heartbeat survey campaign"
        }
        backTo={CAMPAIGNS_PATH}
        backLabel="Back to Campaigns"
        meta={isEdit && formData.isSystem ? <Badge tone="info">System Campaign</Badge> : null}
        actions={
          ready && (
            <>
              {isEdit && formData.isDeletable !== false && (
                <Button
                  variant="secondary"
                  leftIcon={<MdDelete aria-hidden="true" className="h-5 w-5" />}
                  loading={deleting}
                  onClick={handleDelete}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </Button>
              )}
              <Button as={Link} to={CAMPAIGNS_PATH} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                form={FORM_ID}
                loading={saving}
                leftIcon={<MdSave aria-hidden="true" className="h-5 w-5" />}
              >
                {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Campaign"}
              </Button>
            </>
          )
        }
        tabs={
          ready && (
            <div className="space-y-4">
              <CampaignSummary
                formData={formData}
                recipientCount={filterRecipientCount ?? recipientCountCache}
              />
              <Tabs
                id={TABS_ID}
                aria-label="Campaign sections"
                syncParam="tab"
                value={activeTab}
                onChange={setActiveTab}
                tabs={tabs}
              />
            </div>
          )
        }
      />

      {loading ? (
        <EditorSkeleton />
      ) : loadError ? (
        <Card padding="md">
          <ErrorState
            title="Couldn't Load Campaign"
            message={getApiErrorMessage(loadError, "Could not load campaign")}
            onRetry={fetchCampaign}
          />
        </Card>
      ) : (
        <form id={FORM_ID} ref={formRef} noValidate onSubmit={handleSubmit}>
          <TabPanel tabsId={TABS_ID} tabId={activeTab}>
            <ActiveSection editor={editor} />
          </TabPanel>
        </form>
      )}

      <SaveTemplateDialog editor={editor} />
      <StatusConfirmDialog editor={editor} />
    </PageContainer>
  );
};

export default CreateEditCampaign;
